import { useEffect, useState } from 'react';

import { apiRequestBytes } from '@/services/http-client';

interface FotoDoProjetoProps {
  /** Caminho relativo vindo do contrato, já com projeto e imagem. */
  readonly url: string;
  readonly alt: string;
  readonly width: number;
  readonly height: number;
  readonly className?: string;
}

/**
 * Resultado carrega a foto a que pertence.
 *
 * É o que permite trocar de imagem sem zerar o estado dentro do efeito: quando
 * `para` não é a `url` atual, o resultado é de uma foto anterior e a tela mostra
 * o carregamento. Zerar por `setState` no efeito dispararia um render em
 * cascata a cada troca.
 */
type Resultado =
  | { readonly para: string; readonly endereco: string }
  | { readonly para: string; readonly endereco: null };

/**
 * Foto do projeto no painel, buscada com a sessão.
 *
 * Um `<img src>` comum não serve aqui: o navegador não tem como pôr o
 * `Authorization` numa carga de imagem, e sem token a API responde 404 em foto
 * de obra não publicada. Esse 404 está certo — é o que impede o rascunho de
 * vazar pela URL da imagem —, mas o painel é exatamente quem precisa ver o
 * rascunho antes de publicar.
 *
 * Então os bytes vêm por `fetch` autenticado e viram um `object URL`. A API não
 * precisa afrouxar nada, e o cache HTTP continua valendo: a rota responde
 * `immutable`, então a segunda visita sai do disco sem ir à rede.
 *
 * O site público não usa isto e nem deve: lá só existe obra publicada, e um
 * `<img>` direto aproveita `loading="lazy"` e o cache do navegador sem custo
 * nenhum de JavaScript.
 */
export function FotoDoProjeto({ url, alt, width, height, className }: FotoDoProjetoProps) {
  const [resultado, setResultado] = useState<Resultado | null>(null);

  useEffect(() => {
    let ativo = true;
    let criado: string | null = null;

    apiRequestBytes(url)
      .then((bytes) => {
        // Se o componente saiu ou a foto mudou enquanto isto voava, não cria o
        // endereço: criar depois da limpeza vazaria o blob até a aba fechar.
        if (!ativo) return;

        criado = URL.createObjectURL(bytes);
        setResultado({ para: url, endereco: criado });
      })
      .catch(() => {
        if (ativo) setResultado({ para: url, endereco: null });
      });

    return () => {
      ativo = false;

      if (criado) URL.revokeObjectURL(criado);
    };
  }, [url]);

  const atual = resultado?.para === url ? resultado : null;

  // Enquanto carrega, um bloco do mesmo tamanho — senão a lista pula quando
  // cada foto chega.
  if (!atual) {
    return <div aria-hidden="true" className={`animate-pulse bg-sand ${className ?? ''}`} />;
  }

  if (atual.endereco === null) {
    return (
      <div
        role="img"
        aria-label={`Não foi possível carregar a foto: ${alt}`}
        className={`flex items-center justify-center bg-sand text-xs text-ink-soft ${className ?? ''}`}
      >
        falhou
      </div>
    );
  }

  return <img src={atual.endereco} alt={alt} width={width} height={height} className={className} />;
}
